import {
    FC,
    ForwardRefExoticComponent,
    PropsWithChildren,
    ReactNode,
    RefAttributes,
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { SiteContentScopes, TranslationScopes } from "@finbackoffice/enums";
import { usePathname, useRouter } from "next/navigation";
import { formatSearchParamsToString, useSearchParamsQuery } from "@finbackoffice/site-core";
import { IModalForwardRefPropsV2 } from "contexts";
import { usePromotionsData } from "hooks/promotions/usePromotionsData";
import Button from "components/base/button/Button";
import Loading from "components/base/loading/Loading";
import Modal from "components/base/modal/Modal";
import { Svg } from "components/base/svg/Svg";
import { useLoadI18nextResource } from "hooks";
import { ModalTypes } from "utils/constants";
import { ModalStyleTypeEnum } from "components/base/modal/constants/modal-config";
import PromotionsList from "components/bonuses/promotions/list/PromotionsList";
import PromoContent from "components/bonuses/promotions/PromoContent";
import Translate from "components/base/translate/Translate";
import { BonusesPromotionsCasinoGameScreen } from "screens/bonuses/promotions/BonusesPromotionsCasinoGameScreen";
import styles from "./sections.module.sass";

const BonusesModalContent: FC<
    PropsWithChildren & { namespace: TranslationScopes; isLoading: boolean }
> = ({ namespace, isLoading, children }) => {
    const translationScopes = useMemo(() => [namespace], [namespace]);
    const { isTranslationsReady } = useLoadI18nextResource(translationScopes);

    if (!isTranslationsReady || isLoading) {
        return <Loading />;
    }

    return children;
};

type IBonusesModalProps = { handleClose?: () => void };

const BonusesModal: ForwardRefExoticComponent<
    IBonusesModalProps & PropsWithChildren & RefAttributes<IModalForwardRefPropsV2>
> = forwardRef(({ children, handleClose }, ref) => {
    const router = useRouter();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const { scope } = query;

    const handleModalClose = useCallback(() => {
        if (scope) {
            const q = { ...query };
            delete q.scope;
            router.push(`${pathname}?${formatSearchParamsToString(q)}`);
        }
    }, [pathname, query, router, scope]);

    return (
        <Modal
            priority
            ref={ref}
            type={ModalTypes.BONUSES}
            styleClass={styles.profileDetailsModal}
            styleConfigType={ModalStyleTypeEnum.ProfileDetails}
            animateVariant="bottom"
            onClose={handleModalClose}>
            <div data-testid="account-promotions-content">
                {children}
                <Button
                    name="close"
                    type="button"
                    className={styles.closeButton}
                    onClick={handleClose}
                    data-testid="modal-close-button">
                    <Translate tid="base_close" />
                </Button>
            </div>
        </Modal>
    );
});

const PromotionsSection: FC = () => {
    const { query } = useSearchParamsQuery();
    const { scope: scopeParam } = query;
    const [scope, setScope] = useState<SiteContentScopes>((scopeParam as SiteContentScopes) || "");
    const { getPromotionsData, modalBanners, allBonusBanners, isLoading } =
        usePromotionsData(scope);
    const promotionsModalRef = useRef<IModalForwardRefPropsV2>(null);

    useEffect(() => {
        if (scope && promotionsModalRef.current && !!renderModalContent) {
            promotionsModalRef.current?.open();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderModalContent = useMemo((): ReactNode | null => {
        const data = getPromotionsData(scope);
        const { pageName, iconPath, translationScope, casinoGameId } = data || {};

        if (!data || pageName === undefined) {
            return null;
        }

        return (
            <BonusesModalContent
                namespace={translationScope || TranslationScopes.Bonuses}
                isLoading={isLoading}>
                {casinoGameId ? (
                    <BonusesPromotionsCasinoGameScreen
                        banners={modalBanners}
                        gameId={`${casinoGameId}-mobile`}
                        translationScope={translationScope || TranslationScopes.BonusesPromoAviator}
                        promoPageName={pageName}
                    />
                ) : (
                    <PromoContent
                        banners={modalBanners}
                        translationScope={translationScope}
                        promoPageName={pageName}
                        showHeader={false}
                        headingIcon={
                            iconPath && <Svg src={iconPath} className="svg-icon" prefixAppName />
                        }
                    />
                )}
            </BonusesModalContent>
        );
    }, [getPromotionsData, modalBanners, scope, isLoading]);

    return (
        <>
            <nav className={styles.subMenu} data-testid="account-promotions-tab-container">
                <PromotionsList
                    allBanners={allBonusBanners}
                    modalRef={promotionsModalRef}
                    setScope={setScope}
                />
            </nav>
            <BonusesModal
                ref={promotionsModalRef}
                handleClose={() => promotionsModalRef.current?.close()}>
                {renderModalContent}
            </BonusesModal>
        </>
    );
};

export default PromotionsSection;
